import { FC, useMemo, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, CheckCircle, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import useLinkedAccounts from "../hooks/useLinkedAccounts";
import useCard from "../hooks/useCard";
import { useLocation, useNavigate } from "react-router-dom";
import {
  hasStructuredAddress,
  isInternationalResident,
} from "../utils/onboardingNextStep";
import { saveCheckoutResume } from "../utils/checkoutResume";

export const LIVENESS_DEFERRED_KEY = "visaro_liveness_deferred";

const isTruthy = (v: unknown): boolean => {
  if (v === true) return true;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return v.length > 0 && v !== "0";
  return !!v;
};

/* ------------------ METRIC HOOK ------------------ */
export const useAccountSetupMetric = () => {
  const { user } = useAuth();
  const { accounts, fetchingLinkedAccounts } = useLinkedAccounts();
  const { card, isLoading: isLoadingCard } = useCard();

  const isLoading = fetchingLinkedAccounts || isLoadingCard;

  const isLivenessDeferred = localStorage.getItem(LIVENESS_DEFERRED_KEY) === "1";

  const metric = useMemo(() => {
    const hasLinkedAccounts = (accounts?.data?.length || 0) > 0;
    const hasLinkedCard = !!card?.data;
    const international = isInternationalResident(user);

    const checks: Record<string, boolean> = {
      phone_number: !!user?.phone_number,
      phone_verified: !!user?.phone_verified,
      pin_created: !!user?.pin_status,
      email: !!user?.email,
      photo: !!user?.photo,
      username: !!user?.username,
      address: international ? hasStructuredAddress(user) : !!user?.address,
      // Collected at signup, but older accounts can lack it, and the backend
      // installment check (FlightInstallmentEligibility) requires it.
      date_of_birth: !!user?.date_of_birth,
      utility_bill: !!user?.compliance?.utility_image_1 || !!user?.compliance?.utility_status_1,
      card: !!user?.compliance?.verified_card_details || hasLinkedCard,
      bank_account: !!user?.compliance?.bank_account_number,
      employmentDetails: !!user?.compliance?.employment_status || !!user?.compliance?.added_employment_status,
      linked_bank_account: hasLinkedAccounts,
    };

    // BVN and NIN are both Nigerian instruments — a NIN comes from NIMC, a BVN
    // from a Nigerian bank account. Listing them for a customer living abroad
    // would pin their setup progress permanently below 100% with two tasks they
    // have no way to complete.
    if (!international) {
      checks.bvn = isTruthy(user?.compliance?.bvn_verified);
      checks.nin = isTruthy(user?.compliance?.nin_verified);
    } else {
      checks.passport = !!(
        user?.compliance?.passport_number &&
        user?.compliance?.passport_issuing_country &&
        user?.compliance?.passport_expires_on &&
        new Date(user.compliance.passport_expires_on).getTime() > Date.now() &&
        user?.compliance?.passport_document_key
      );
    }

    // Only include liveness in progress tracking if not deferred
    if (!isLivenessDeferred) {
      checks.liveness = Number(user?.compliance?.verified_face) === 1;
    }

    const done = Object.values(checks).filter(Boolean).length;
    const all = Object.keys(checks).length;

      return { done, all, checks, isLoading, isLivenessDeferred, international };
    }, [user, accounts, card, isLoading, isLivenessDeferred]);

  return metric;
};

/* ------------------ TASKS ------------------ */
export type SetupTask = {
  label: string;
  description: string;
  completed: boolean;
  route: string;
  isModal?: boolean;
  modalName?: string;
};

/**
 * The setup tasks in product order (see brain.md #187). Shared by the
 * dashboard checklist and the installment gate, so both always agree on what
 * "the first task" is and what is left.
 */
export const useAccountSetupTasks = () => {
  const metric = useAccountSetupMetric();

  const tasks: SetupTask[] = [
    // Product-set order: selfie, verify phone, PIN, BVN, link bank account,
    // link card, username. Everything else follows.
    {
      label: "Add Profile Picture",
      description: "Take a selfie or upload a clear photo of yourself",
      completed: metric.checks.photo,
      route: "/upload-profile-picture",
      isModal: true,
      modalName: "upload-profile-picture",
    },
    {
      label: "Verify Phone Number",
      description: "Verify your phone number",
      completed: metric.checks.phone_verified,
      route: "/home/settings#phone_number",
    },
    {
      label: "Setup your PIN",
      description: "Create a 4-digit PIN for transactions",
      completed: metric.checks.pin_created,
      route: "/pin/setup",
    },
    // BVN and NIN only exist in Nigeria — see the metric above for why they are
    // dropped entirely rather than shown as permanently incomplete.
    ...(!metric.international
      ? [
          {
            label: "Verify your BVN",
            description: "Keep your account more secure",
            completed: metric.checks.bvn,
            route: "/verify-bvn",
            isModal: true,
            modalName: "verify-bvn",
          },
        ]
      : []),
    {
      label: "Link your Bank Account",
      description: "Link your bank accounts safely to build credit",
      completed: metric.checks.linked_bank_account,
      route: "/home/settings/compliance/bank-accounts",
    },
    {
      label: "Link your Card",
      description: "Link your card to your account",
      completed: metric.checks.card,
      route: "/wallet",
    },
    {
      label: "Create your Username",
      description:
        "A unique username that will be associated with your account",
      completed: metric.checks.username,
      route: "/home/settings#username",
    },
    ...(!metric.international
      ? [
          {
            label: "Verify your NIN",
            description: "Keep your account more secure by verifying your identity",
            completed: metric.checks.nin,
            route: "/verify-nin",
            isModal: true,
            modalName: "verify-nin",
          },
        ]
      : []),
    {
      label: "Add Home Address",
      description: metric.international
        ? "Your address and postcode, used to verify your identity"
        : "Add your home address",
      completed: metric.checks.address,
      // Customers abroad go to the structured form, which knows what each
      // country's address actually needs. The settings anchor stays for
      // Nigerian customers, whose existing flow is unchanged.
      route: metric.international
        ? "/home/settings/compliance/home-address"
        : "/home/settings#address",
    },
    {
      label: "Add Date of Birth",
      description: "Needed to verify your identity",
      completed: metric.checks.date_of_birth,
      route: "/home/settings#date_of_birth",
    },
    ...(metric.international
      ? [{
          label: "Submit your Passport",
          description: "International-customer identity requirement",
          completed: metric.checks.passport,
          route: "/home/settings/compliance/travel-document",
        }]
      : []),
    {
      label: "Proof of Address",
      description: "Upload a utility bill as proof of address",
      completed: metric.checks.utility_bill,
      route: "/home/settings/compliance/utility-bill",
    },
    {
      label: "Add Phone Number",
      description: "Keep your account more secure by adding a phone number",
      completed: metric.checks.phone_number,
      route: "/home/settings#phone_number",
    },
    ...(!metric.isLivenessDeferred
      ? [
          {
            label: "Liveness Verification",
            description: "Complete a quick face scan to secure your account",
            completed: metric.checks.liveness ?? false,
            route: "/verify/liveness",
          },
        ]
      : []),
    {
      label: "Add Bank Account Details",
      description: "Add your bank account details",
      completed: metric.checks.bank_account,
      route: "/verify-bank",
      isModal: true,
      modalName: "verify-bank",
    },
    {
      label: "Add Employment Details",
      description: "Add your employment details",
      completed: metric.checks.employmentDetails,
      route: "/home/settings/compliance/employment-details",
    },
  ];

  return { tasks, metric };
};

export type SetupTaskReturn = {
  returnTo?: string;
  returnState?: unknown;
  /** The checkout's booking, saved so an off-site task (Mono, Paystack) can resume it. */
  bookingId?: string;
};

/** Open a task's screen; `returnTo` brings the customer back afterwards. */
export const useStartSetupTask = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  return (task: SetupTask, options?: SetupTaskReturn) => {
    const back = options?.returnTo
      ? { returnTo: options.returnTo, returnState: options.returnState }
      : {};
    if (options?.returnTo && options.bookingId && user?.id != null) {
      saveCheckoutResume({
        userId: String(user.id),
        bookingId: options.bookingId,
        returnTo: options.returnTo,
        returnState: options.returnState,
      });
    }
    if (task.isModal) {
      navigate(task.route, {
        state: {
          background: location,
          name: task.modalName,
          fromSetup: true,
          ...back,
        },
      });
    } else if (options?.returnTo && task.route.startsWith("/verify/")) {
      // The /verify screens return via a `redirect` query, not route state.
      navigate(
        `${task.route}?redirect=${encodeURIComponent(options.returnTo)}`,
        { state: { fromSetup: true, ...back } },
      );
    } else {
      navigate(task.route, { state: { fromSetup: true, ...back } });
    }
  };
};

/* ------------------ COMPONENT ------------------ */
const CompleteSetupTaskList: FC<{
  isOpen: boolean;
  handleClose: () => void;
  /** Overrides the header, e.g. when opened from the installment gate. */
  title?: string;
  subtitle?: string;
  /** Where each task returns the customer once done (default: dashboard). */
  returnTo?: string;
  returnState?: unknown;
  bookingId?: string;
}> = ({
  isOpen,
  handleClose,
  title,
  subtitle,
  returnTo,
  returnState,
  bookingId,
}) => {
  const { user } = useAuth();
  const { tasks, metric } = useAccountSetupTasks();
  const startTask = useStartSetupTask();

  useEffect(() => {
    if (isOpen && metric.all !== metric.done && !metric.isLoading) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, metric.all, metric.done, metric.isLoading]);

  /* -------- stop rendering -------- */
  if (metric.all === metric.done || !isOpen || metric.isLoading) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="fixed inset-0 bg-black/30 z-[99998] backdrop-blur-sm overflow-y-auto p-4 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          >

          {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="bg-white drop-shadow-sm rounded-xl flex flex-col gap-4 max-w-2xl w-full max-h-[85vh] sm:max-h-[42rem] overflow-hidden my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex gap-5 items-center shrink-0 bg-white px-5 pt-5 pb-2 border-b">
                <div className="text-visaro-orange">
                  <CheckCircle weight="fill" size={40} />
                </div>
                <div className="flex flex-col flex-1">
                  <div className="text-lg text-deepBlue font-medium">
                    {title ?? "Finish setting up your account"}
                  </div>
                  <div className="text-gray-500 text-sm">
                    {subtitle ??
                      "You’re on a good start! Unlock secure payments, instant credit, and more."}
                  </div>
                </div>
                {/* X hidden on mobile — mobile uses the fixed bottom Close button */}
                <button
                  onClick={handleClose}
                  className="hidden sm:block p-2 bg-gray-200/50 rounded-full hover:bg-gray-300 transition"
                >
                  <X weight="bold" size={20} />
                </button>
              </div>

              {/* Progress */}
              <div className="flex gap-4 items-center px-5 shrink-0">
                <div className="flex flex-col flex-1 gap-2">
                  <div className="h-2 rounded bg-gray-200 w-full">
                    <motion.div
                      animate={{
                        width: `${((metric.done / metric.all) * 100).toFixed(
                          0
                        )}%`,
                      }}
                      className="h-full rounded bg-visaro-orange"
                    />
                  </div>
                  <div className="text-gray-500 text-sm">
                    {((metric.done / metric.all) * 100).toFixed(0)}% complete
                  </div>
                </div>
              </div>

              {/* Tasks */}
              {user && (
                <div className="flex flex-col gap-2 px-5 pb-4 flex-1 overflow-y-auto min-h-0">
                  <AnimatePresence>
                    {tasks.map(
                      (task, index) =>
                        !task.completed && (
                          <motion.button
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => {
                              handleClose();
                              startTask(task, { returnTo, returnState, bookingId });
                            }}
                            className="p-2 flex items-center gap-2 justify-between rounded-lg hover:bg-gray-100 transition text-left w-full"
                          >
                            <div>
                              <div className="font-medium">{task.label}</div>
                              <div className="text-sm">{task.description}</div>
                            </div>
                            <ArrowRight className="text-gray-500" size={22} />
                          </motion.button>
                        )
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Footer — mobile-only Close button, fixed at the bottom while
                  the tasks area above scrolls. Desktop closes via the header X. */}
              <div className="px-5 py-3 border-t shrink-0 sm:hidden">
                <button
                  onClick={handleClose}
                  className="w-full py-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-deepBlue font-medium transition"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CompleteSetupTaskList;
